import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';
// @mui
import { alpha, styled } from '@mui/material/styles';
import { Box, Card, Chip, Grid, Stack, Avatar, Typography, CardContent } from '@mui/material';
// utils
import { fDate } from '../../../utils/formatTime';
import { fShortenNumber } from '../../../utils/formatNumber';
// components
import SvgColor from '../../../components/svg-color';
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
// utils
import { DEFAULT_BLOG_AVATAR, DEFAULT_BLOG_COVER } from '../../../utils/blog';

// ----------------------------------------------------------------------

const StyledCardMedia = styled('div')({
  position: 'relative',
  aspectRatio: '3 / 2',
});

const StyledCoverWrapper = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  overflow: 'hidden',
});

const StyledTitle = styled(Typography)({
  overflow: 'hidden',
  minHeight: 44,
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledExcerpt = styled(Typography)({
  overflow: 'hidden',
  WebkitLineClamp: 2,
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
});

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  zIndex: 9,
  width: 40,
  height: 40,
  position: 'absolute',
  left: theme.spacing(3),
  bottom: theme.spacing(-2),
}));

const StyledInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
  color: theme.palette.text.disabled,
}));

const StyledCover = styled('img')({
  top: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
  transition: 'transform 0.6s cubic-bezier(0.43, 0.13, 0.23, 0.96)',
});

// ----------------------------------------------------------------------

BlogPostCard.propTypes = {
  post: PropTypes.object.isRequired,
  index: PropTypes.number,
};

export default function BlogPostCard({ post, index = 0 }) {
  const { slug, cover, title, excerpt, category, readTime, view, comment, author, createdAt } = post ?? {};
  const authorName = author?.name ?? 'ASAP UAE';

  const stats = [
    { number: comment, icon: 'eva:message-circle-fill' },
    { number: view, icon: 'eva:eye-fill' },
  ];

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.08 }}
        sx={{ height: '100%' }}
      >
        <Card
          component={RouterLink}
          to={`/blog/${slug}`}
          aria-label={`Read article: ${title}`}
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            color: 'inherit',
            textDecoration: 'none',
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '&:focus-visible': {
              outline: (theme) => `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 4,
            },
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.16)}`,
              },
              '&:hover img': { transform: 'scale(1.06)' },
              '&:hover .read-arrow': { transform: 'translateX(4px)' },
            },
          }}
        >
          <StyledCardMedia>
            <StyledCoverWrapper>
              <StyledCover
                alt={title}
                src={cover}
                loading="lazy"
                decoding="async"
                onError={(event) => { event.currentTarget.src = DEFAULT_BLOG_COVER; }}
              />
            </StyledCoverWrapper>

            <SvgColor
              color="paper"
              src="/assets/icons/shape-avatar.svg"
              sx={{
                width: 80,
                height: 36,
                zIndex: 9,
                bottom: -15,
                position: 'absolute',
                color: 'background.paper',
              }}
            />
            <StyledAvatar
              alt={authorName}
              src={author?.avatarUrl}
              imgProps={{ onError: (event) => { event.currentTarget.src = DEFAULT_BLOG_AVATAR; } }}
            >
              {authorName.charAt(0)}
            </StyledAvatar>
          </StyledCardMedia>

          <CardContent sx={{ pt: 4, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 1, alignItems: 'center', justifyContent: 'space-between' }}
            >
              <Chip
                label={category}
                size="small"
                sx={{
                  height: 22,
                  maxWidth: '70%',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  color: 'primary.main',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
              <Typography variant="caption" sx={{ color: 'text.disabled', whiteSpace: 'nowrap' }}>
                {readTime} min read
              </Typography>
            </Stack>

            <StyledTitle variant="subtitle1" sx={{ color: 'text.primary', mb: 1 }}>
              {title}
            </StyledTitle>

            <StyledExcerpt variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              {excerpt}
            </StyledExcerpt>

            <StyledInfo>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {authorName} · {fDate(createdAt)}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {stats.map((item, statIndex) => (
                  <Box
                    key={item.icon}
                    sx={{ display: 'flex', alignItems: 'center', ml: statIndex === 0 ? 0 : 1.5 }}
                  >
                    <Iconify icon={item.icon} sx={{ width: 16, height: 16, mr: 0.5 }} />
                    <Typography variant="caption">{fShortenNumber(item.number)}</Typography>
                  </Box>
                ))}
              </Box>
            </StyledInfo>

            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 1.5, color: 'primary.main', alignItems: 'center' }}
            >
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                Read article
              </Typography>
              <Iconify
                className="read-arrow"
                icon="eva:arrow-forward-outline"
                sx={{ width: 16, height: 16, transition: 'transform 0.25s ease' }}
              />
            </Stack>
          </CardContent>
        </Card>
      </MotionInView>
    </Grid>
  );
}
